import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { PartyEvent } from "../../model/PartyEvent";
import { RootState } from '../../app/store';
import { getPartyEventsAsync, insertPartyEventAsync, SavePartyEventDto, deletePartyEventAsync, getBackEndVersionNumberAsync } from './partyEventsAPI';
import { findItemIndexById, removeItemAtIndex, insertItemAtIndex, comparePartyEventStarts } from "../../utils/arrayUtils"
import { PartyEvents } from './PartyEvents';
import { dateToTransmissionDateTime } from '../../utils/dateUtils';
import { menuSlice } from '../menu/menuSlice';

export interface PartyEventsState {
    partyEvents: PartyEvent[]
    filteredPartyEvents: PartyEvent[]
    status: 'idle' | 'loading' | 'failed'
    retrievedFromServer: boolean
    backEndVersionNumber: string
    filter: string
    displayFilter: boolean
    refreshRequired: boolean
    sortDescending : boolean
}

const initialState: PartyEventsState = {
    partyEvents: [],
    filteredPartyEvents:[],
    status: 'idle',
    retrievedFromServer: false,
    backEndVersionNumber: '',
    filter:'',
    displayFilter: false,
    refreshRequired: true,
    sortDescending: false
}

export interface GetPartyEventsRequest {
  email: string
  token: string
}

export interface AddPartyEventRequest {
  partyEvent: PartyEvent
  token: string
  userEmail: string
}

export interface DeletePartyEventRequest {
  partyEventId: number
  token: string
  userEmail: string
}

export interface DeletePartyEventResponse {
  partyEventId: number
  success: boolean
}

export interface GetBackendVersionNumberRequest {
  token : string
}
// The function below is called a thunk and allows us to perform async logic. It
// can be dispatched like a regular action: `dispatch(incrementAsync(10))`. This
// will call the thunk with the `dispatch` function as the first argument. Async
// code can then be executed and other actions can be dispatched. Thunks are
// typically used to make async requests.
export const loadPartyEventsAsync = createAsyncThunk(
    'partyEvents/fetchPartyEvents',
    async (request: GetPartyEventsRequest) => {
      
      const response = await getPartyEventsAsync(request.email, request.token)
      // The value we return becomes the `fulfilled` action payload
      
      
      return response;
    }
  );

export const addPartyEventAsync = createAsyncThunk(
    'partyEvents/addPartyEvents',
    async (request: AddPartyEventRequest) => {
      let savePartyEventDto: SavePartyEventDto = {
        id: request.partyEvent.id,
        title: request.partyEvent.title,
        location: request.partyEvent.location,
        start: request.partyEvent.start,
        end: request.partyEvent.end,
        userEmail: request.userEmail 
      }
      const response = await insertPartyEventAsync(savePartyEventDto, request.token,)
      return response;
    }
  )

export const removeEventAsync = createAsyncThunk(
  'partyEvents/removeEventsAsync',
  async (request: DeletePartyEventRequest) => {
    const apiResponse = await deletePartyEventAsync(request.partyEventId, request.userEmail, request.token)
    const response: DeletePartyEventResponse = {
      partyEventId: request.partyEventId,
      success: apiResponse
    }
    return response
  }
)

export const retrieveBackEndVersionNumberAsync = createAsyncThunk(
    'partyEvents/retrieveBackEndVersionNumberAsync',
    async (request: GetBackendVersionNumberRequest) => {
      const response = await getBackEndVersionNumberAsync(request.token)
      return response
    }
)

export function addPartyEventInOrder(partyEvent : PartyEvent, destination : PartyEvent[]) {
  if (!partyEvent.start) {
      destination.push(partyEvent)
      return 
  }
  for (let i = 0; i<destination.length ; i ++) {
    console.log(partyEvent.title + ' ' + partyEvent.start + ' - ' + i + ' ' + destination[i].title + ' ' + destination[i].start)
    if (partyEvent.start < (destination[i].start ?? '0000-01-01T00:00:00') ) {
        destination = insertItemAtIndex(destination, partyEvent, i)
        return
    }
  }
  destination.push(partyEvent)
}

export function filterAndSort(originalList: PartyEvent[], filter: string, sortDescending: boolean) {
  function sort (a : PartyEvent, b : PartyEvent) {
    return comparePartyEventStarts(a, b, sortDescending)
  }
  let filteredPartyEvents: PartyEvent[] = []
    if (filter.trim().length==0) {
      filteredPartyEvents = originalList
    }
    else {
      let capitalFilter : string = filter.toUpperCase()
      originalList.map((partyEvent) => {
        if ( partyEvent.title.toUpperCase().indexOf(capitalFilter)>-1) {
          filteredPartyEvents.push(partyEvent)
        }
      }
  ) }
  filteredPartyEvents.sort(sort)
   return filteredPartyEvents
}

export const partyEventsSlice = createSlice({
    name: 'partyEvents',
    initialState: initialState,
    reducers: {

      setDisplayFilter : (state, action: PayloadAction<boolean>) => {
        state.displayFilter = action.payload
      },
      setPartyEventsFilter: (state, action: PayloadAction<string> ) => {
        state.filter = action.payload
        state.filteredPartyEvents = filterAndSort(state.partyEvents, state.filter, state.sortDescending)
      },
      setSortDescending: (state, action: PayloadAction<boolean>) => {
        state.sortDescending = action.payload
        state.filteredPartyEvents = filterAndSort(state.partyEvents, state.filter, state.sortDescending)
      }
    },

    // The `extraReducers` field lets the slice handle actions defined elsewhere,
    // including actions generated by createAsyncThunk or in other slices.
    extraReducers: (builder) => {
      builder
        .addCase(loadPartyEventsAsync.pending, (state) => {
          state.status = 'loading';
        })
        .addCase(loadPartyEventsAsync.fulfilled, (state, action) => { 
          state.retrievedFromServer = true
          state.refreshRequired = false
          state.status = 'idle';
          action.payload.map(partyEvent => {
            if (partyEvent.start) {
            partyEvent.start = partyEvent.start.substring(0,19)
            } 
            if (partyEvent.end) {
              partyEvent.end = partyEvent.end.substring(0, 19)
              }
          })
          state.partyEvents = action.payload;
          state.filter = '';
          state.filteredPartyEvents = filterAndSort(state.partyEvents, state.filter, state.sortDescending)
        })
        .addCase(loadPartyEventsAsync.rejected, (state) => {
          state.status = 'failed';
        })
        .addCase(addPartyEventAsync.pending, (state) => {
          state.status = 'loading';
        })
        .addCase(addPartyEventAsync.fulfilled, (state, action) => {
          state.status = 'idle';
          if (action.payload != null) {
            state.partyEvents.push(action.payload);
            state.refreshRequired = true
          }
        })
        .addCase(addPartyEventAsync.rejected, (state) => {
          state.status = 'failed';
          alert('Internal error occured saving Party')
        })
        .addCase(removeEventAsync.pending, (state) => {
          state.status = 'loading';
        })
        .addCase(removeEventAsync.fulfilled, (state, action) => {
          state.status = 'idle';
          if (action.payload.success) {
            const index = findItemIndexById(state.partyEvents, action.payload.partyEventId)
            state.partyEvents = removeItemAtIndex(state.partyEvents, index)
            state.refreshRequired = true
          }
        })
        .addCase(removeEventAsync.rejected, (state) => {
          state.status = 'failed';
          console.log('Deletion of event failed')
        })
        .addCase(retrieveBackEndVersionNumberAsync.pending, (state) => {
          state.status = 'loading'
        })
        .addCase(retrieveBackEndVersionNumberAsync.fulfilled, (state ,action) => {
          state.status = 'idle'
          state.backEndVersionNumber = action.payload
        })
        .addCase(retrieveBackEndVersionNumberAsync.rejected, (state) => {
          state.status = 'failed'
        })
    },
})

// The function below is called a selector and allows us to select a value from
// the state. Selectors can also be defined inline where they're used instead of
// in the slice file. For example: `useSelector((state: RootState) => state.counter.value)`
export const selectPartyEvents = (state: RootState) => state.partyEvents.partyEvents;
export const selectPartyEventsCount = (state: RootState) => state.partyEvents.partyEvents.length;
export const selectPartyEventsRetrieved = (state: RootState) => state.partyEvents.retrievedFromServer;
export const selectPartyEventsStatus = (state: RootState) => state.partyEvents.status;
export const selectBackEndVersionNumber = (state: RootState) => state.partyEvents.backEndVersionNumber;
export const selectPartyEventsFilter = (state: RootState) => state.partyEvents.filter;
export const selectFilteredPartyEvents = (state: RootState) => state.partyEvents.filteredPartyEvents;
export const selectDisplayFilter = (state: RootState) => state.partyEvents.displayFilter;
export const selectRefreshRequired = (state: RootState) => state.partyEvents.refreshRequired;
export const selectSortDecending = (state: RootState) => state.partyEvents.sortDescending;

export const {
  setPartyEventsFilter,
  setDisplayFilter,
  setSortDescending
} = partyEventsSlice.actions
export default partyEventsSlice.reducer;